import { updateGridPositionConfig } from '../grid/reduxStore/editorSlice.ts';
import { useAppDispatch } from '../grid/reduxStore/Store.ts';
import { useCurrentUser } from '../../../providers/UserProvider.tsx';
import { useSocketClient } from '../../../providers/SocketContext.tsx';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';
import { setSaveStatus } from '../grid/reduxStore/saveStatusSlice.ts';
import { DocumentSaveStatus } from '../shared/models/DocumentSaveStatus.ts';

import { useCallback } from 'react';
import { UndoRedoManagerCommandVersion } from '../UndoRedo/UndoRedoManagerCommandVersion.ts';
import { updateBlockPositionViaSocket } from '../grid/reduxStore/saveHandlers.ts';
import { UpdateBlockPositionCommand } from '../UndoRedo/UpdateBlockPositionCommand.ts';

export type PositionStateType = {
  xAxisPx: number;
  yAxisPx: number;
};

export function useNewBlockPositionChangedHandler() {
  const dispatch = useAppDispatch();
  const { data } = useCurrentUser();
  const userId = data.id;
  const { socketClient } = useSocketClient();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();

  return useCallback(
    async (sectionId: string, blockId: string, positionBefore: PositionStateType, positionAfter: PositionStateType): Promise<void> => {
      const undoRedoManager = UndoRedoManagerCommandVersion.getUndoRedoManager();

      const command = new UpdateBlockPositionCommand(blockId, sectionId, positionBefore, positionAfter, {
        dispatch,
        socketClient,
        userId,
        checkDocumentLockStatus,
      });

      const success = await command.redo();

      if (success) {
        undoRedoManager.pushUndoRedoCommands(command);
      }
    },
    [dispatch, socketClient, userId, checkDocumentLockStatus]
  );
}

export function useNewBlockPositionChangedHandlerWithoutUndoRedo() {
  const dispatch = useAppDispatch();
  const { data } = useCurrentUser();
  const userId = data.id;
  const { socketClient } = useSocketClient();
  const checkDocumentLockStatus = useCheckDocumentLockStatus();

  return useCallback(
    async (sectionId: string, blockId: string, position: PositionStateType): Promise<void> => {
      dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      dispatch(
        updateGridPositionConfig({
          sectionId,
          blockId,
          x: position.xAxisPx,
          y: position.yAxisPx,
        })
      );

      const data = {
        blockId,
        userId,
        position: {
          top_px: position.yAxisPx,
          left_px: position.xAxisPx,
        },
      };
      await updateBlockPositionViaSocket(socketClient, data);

      return;
    },
    [dispatch, socketClient, userId, checkDocumentLockStatus]
  );
}
